<template>
  <div class="page-left" :class="{ collapsed }">
    <div class="logo" :title="collapsed ? '警消互助平台' : ''">
      <img :src="logoImage" alt="logo" class="logo-image" />
      <span v-show="!collapsed" class="logo-text">logo</span>
    </div>

    <ul class="menu-list">
      <li
        v-for="group in menuGroups"
        :key="group.key"
        class="menu-item"
        :class="{ active: activeMenu === group.key }"
      >
        <div
          class="menu-title"
          :title="collapsed ? group.title : ''"
          @click="handleMenuClick(group)"
        >
          <component :is="group.icon" class="menu-icon" />
          <span v-show="!collapsed" class="menu-label">{{ group.title }}</span>
        </div>

        <ul class="sub-menu" v-show="!collapsed && activeMenu === group.key">
          <li
            v-for="subMenu in group.subMenus"
            :key="subMenu.key"
            class="sub-menu-item"
            :class="{ active: activeSubMenu === subMenu.key }"
            @click="selectSubMenu(group.key, subMenu.key, subMenu.path)"
          >
            {{ subMenu.label }}
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { SettingOutlined, BookOutlined, TeamOutlined } from '@ant-design/icons-vue'
import logoImage from '@/assets/logo.png'
import { getEntryByProtectedPath, getLoginEntry } from '@/utils/loginEntry'

const ADMIN_MENUS = [
  {
    key: 'account',
    title: '账号管理',
    icon: SettingOutlined,
    defaultSubMenu: 'manageAccount',
    defaultPath: '/admin/manageAccount',
    subMenus: [
      { key: 'manageAccount', label: '账号列表', path: '/admin/manageAccount' }
    ]
  },
  {
    key: 'product',
    title: '产品知识库',
    icon: BookOutlined,
    defaultSubMenu: 'productList',
    defaultPath: '/admin/productList',
    subMenus: [
      { key: 'productList', label: '产品列表', path: '/admin/productList' }
    ]
  },
  {
    key: 'dictionary',
    title: '字典管理',
    icon: BookOutlined,
    defaultSubMenu: 'dictionaryList',
    defaultPath: '/admin/dictionaryList',
    subMenus: [
      { key: 'dictionaryList', label: '字典列表', path: '/admin/dictionaryList' }
    ]
  },
  {
    key: 'clue',
    title: '线索管理',
    icon: TeamOutlined,
    defaultSubMenu: 'clueList',
    defaultPath: '/admin/clueList',
    subMenus: [
      { key: 'clueList', label: '线索列表', path: '/admin/clueList' },
      { key: 'clueGroupList', label: '线索分组', path: '/admin/clueGroupList' }
    ]
  }
]

const REPORT_MENUS = [
  {
    key: 'reportCenter',
    title: '举报中心',
    icon: TeamOutlined,
    defaultSubMenu: 'reportCenter',
    defaultPath: '/report/reportCenter',
    subMenus: [
      { key: 'reportCenter', label: '我的举报', path: '/report/reportCenter' }
    ]
  }
]

const VERIFY_MENUS = [
  {
    key: 'verifyCenter',
    title: '核证中心',
    icon: SettingOutlined,
    defaultSubMenu: 'verifyCenter',
    defaultPath: '/verify/verifyCenter',
    subMenus: [
      { key: 'verifyCenter', label: '核证工作台', path: '/verify/verifyCenter' }
    ]
  }
]

export default {
  name: 'PageLeft',
  props: {
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const activeMenu = ref('account')
    const activeSubMenu = ref('manageAccount')

    const currentEntry = computed(() => getEntryByProtectedPath(route.path) || getLoginEntry())
    const menuGroups = computed(() => {
      if (currentEntry.value === 'report') {
        return REPORT_MENUS
      }

      if (currentEntry.value === 'verify') {
        return VERIFY_MENUS
      }

      return ADMIN_MENUS
    })

    const selectSubMenu = (menu, subMenu, path = '') => {
      activeMenu.value = menu
      activeSubMenu.value = subMenu

      if (path && route.path !== path) {
        router.push(path)
      }
    }

    const handleMenuClick = (group) => {
      selectSubMenu(group.key, group.defaultSubMenu, group.defaultPath)
    }

    const syncMenuByRoute = (path) => {
      const matchedGroup = menuGroups.value.find((group) =>
        group.subMenus.some((subMenu) => subMenu.path === path)
      )

      if (matchedGroup) {
        const matchedSubMenu = matchedGroup.subMenus.find((subMenu) => subMenu.path === path)
        activeMenu.value = matchedGroup.key
        activeSubMenu.value = matchedSubMenu ? matchedSubMenu.key : matchedGroup.defaultSubMenu
        return
      }

      const fallbackGroup = menuGroups.value[0]
      if (fallbackGroup) {
        activeMenu.value = fallbackGroup.key
        activeSubMenu.value = fallbackGroup.defaultSubMenu
      }
    }

    watch(
      [() => route.path, menuGroups],
      ([path]) => {
        syncMenuByRoute(path)
      },
      { immediate: true }
    )

    return {
      logoImage,
      menuGroups,
      activeMenu,
      activeSubMenu,
      handleMenuClick,
      selectSubMenu
    }
  }
}
</script>

<style lang="scss" scoped>
.page-left {
  width: 160px;
  height: 100vh;
  flex-shrink: 0;
  background-color: #ffffff;
  border-right: 1px solid #e8edf3;
  color: #333;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0;
  padding: 0;
  transition: width 0.2s ease;
}

.page-left.collapsed {
  width: 64px;
}

.logo {
  height: 64px;
  padding: 0 18px;
  margin: 0;
  color: #1e2a3a;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e8edf3;
}

.page-left.collapsed .logo {
  padding: 0;
  justify-content: center;
}

.logo-image {
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
}

.logo-text {
  white-space: nowrap;
  font-size: 18px;
  font-weight: 600;
}

.menu-list {
  flex: 1;
  padding: 0;
  margin: 0;
  list-style: none;
}

.menu-item {
  margin-bottom: 0;
}

.menu-item .menu-title {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.2s;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.page-left.collapsed .menu-item .menu-title {
  padding: 12px 0;
  justify-content: center;
}

.menu-item .menu-title:hover {
  background-color: #f5f7fa;
  color: #1890ff;
}

.menu-item.active .menu-title {
  background-color: #caeeff;
  color: #1890ff;
  font-weight: 600;
}

.menu-icon {
  margin-right: 12px;
  font-size: 16px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-left.collapsed .menu-icon {
  margin-right: 0;
}

.sub-menu {
  list-style: none;
  margin: 0;
  padding: 0;
  background-color: #fafafa;
}

.page-left.collapsed .sub-menu {
  display: none !important;
}

.sub-menu .sub-menu-item {
  padding: 10px 20px 10px 56px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
  white-space: nowrap;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-weight: 400;
}

.sub-menu .sub-menu-item:hover {
  background-color: #f5f7fa;
  color: #1890ff;
}

.sub-menu .sub-menu-item.active {
  background-color: #f0f9ff;
  color: #1890ff;
  font-weight: 500;
  border-left: 3px solid #1890ff;
  padding-left: 53px;
}
</style>
